import { round4 } from '../catalog/catalog';
import type { ApprovalView } from './approvalGate';
import type { PrintSheetLayout, PrintSheetHtml } from '../print/templates';
import { termDays } from './invoice';
import { roundHalfUpCents } from '../money';
import type { BillLineVariance } from './billVariance';
import type { RoutingOutcome } from './approvalGate';
import type { DividerRow } from '../composites/itemTable';
import type { DocumentFileRow } from './salesOrder';

/**
 * The Bill and its landed costs (piece 22, Bill and landed costs; decisions
 * 0214, 0220). 🔨 Freight, shipping and customs are their own lines flagged
 * `isLandedCost` and allocate proportionately across the goods lines by
 * extended cost, the rounding remainder to the largest line. 🔨 A landed-cost
 * line never allocates to another landed-cost line, and freight on a Bill
 * with no goods lines is an expense rather than a cost to land anything.
 */
export const BILL_KINDS = ['againstPo', 'beforeReceipt', 'noPo', 'fixedAsset', 'dropShip'] as const;
export type BillKindValue = (typeof BILL_KINDS)[number];

/** The two fixed-asset accounts a Bill line codes to, which route it away from 2050 and 1200 (decision 0220). */
export const FIXED_ASSET_ACCOUNT_NUMBERS = ['1500', '1510'] as const;

export interface BillLineFacts {
  id: string;
  isLandedCost: boolean;
  /** quantity × unitPrice, integer cents; the extended cost the allocation weighs by. */
  extendedCents: number;
}

export interface LandedAllocation {
  lineId: string;
  allocatedCents: number;
}

export interface LandedSpread {
  /** The landed total spread across the goods lines; zero when there is nothing to land. */
  landedTotalCents: number;
  allocations: readonly LandedAllocation[];
  /** 🔨 True when the Bill carries no goods line: the freight is an expense and lands nothing. */
  expensed: boolean;
}

/**
 * The spread: each goods line takes the landed total in the proportion its
 * extended cost bears to the goods total, and the rounding remainder lands on
 * the largest goods line, so the allocations sum to the landed total exactly.
 */
export function spreadLandedCost(lines: readonly BillLineFacts[]): LandedSpread {
  const goods = lines.filter((line) => !line.isLandedCost);
  const landedTotalCents = lines.filter((line) => line.isLandedCost).reduce((sum, line) => sum + line.extendedCents, 0);
  if (goods.length === 0) return { landedTotalCents: 0, allocations: [], expensed: landedTotalCents !== 0 };
  if (landedTotalCents === 0) return { landedTotalCents: 0, allocations: goods.map((line) => ({ lineId: line.id, allocatedCents: 0 })), expensed: false };
  const goodsTotal = goods.reduce((sum, line) => sum + line.extendedCents, 0);
  const allocations = goods.map((line) => ({ lineId: line.id, allocatedCents: goodsTotal === 0 ? 0 : roundHalfUpCents((landedTotalCents * line.extendedCents) / goodsTotal) }));
  const spread = allocations.reduce((sum, row) => sum + row.allocatedCents, 0);
  if (spread !== landedTotalCents) {
    // 🔨 The remainder goes to the largest line, so the allocation sums to the landed total to the cent.
    let largest = 0;
    for (let i = 1; i < goods.length; i += 1) if (goods[i]!.extendedCents > goods[largest]!.extendedCents) largest = i;
    allocations[largest]!.allocatedCents += landedTotalCents - spread;
  }
  return { landedTotalCents, allocations, expensed: false };
}

/** The landed unit cost the repricing proposal of decision 0214 reads: the line's own price plus its share of the freight, per unit. */
export function landedUnitCost(unitPrice: number, allocatedCents: number, quantity: number): number {
  if (!(quantity > 0)) return unitPrice;
  return round4(unitPrice + allocatedCents / 100 / quantity);
}

export interface BillRouteFacts {
  hasPurchaseOrder: boolean;
  isDropShip: boolean;
  /**
   * 🔨 True once THIS line's own matched Purchase Order line has received
   * something. Routing on the order as a whole billed an unreceived line
   * against 2050's received value and sent the whole of it to 5000 as a
   * variance (found in review, 2026-09-15).
   */
  lineReceived: boolean;
  /** False on a line naming no Item, and on one whose Item is on no line of the order: neither has goods behind it. */
  matchedToOrderLine: boolean;
  /** The account the line resolved to at creation; a fixed-asset one routes the line to 1500 or 1510. */
  accountNumber: string | null;
}

/** Which row of the posting map one Bill line routes through; a Bill carrying two kinds posts one entry per kind. */
export function billLineKind(facts: BillRouteFacts): BillKindValue {
  if (facts.isDropShip) return 'dropShip';
  if (facts.accountNumber !== null && (FIXED_ASSET_ACCOUNT_NUMBERS as readonly string[]).includes(facts.accountNumber)) return 'fixedAsset';
  if (!facts.hasPurchaseOrder || !facts.matchedToOrderLine) return 'noPo';
  return facts.lineReceived ? 'againstPo' : 'beforeReceipt';
}

/** What a Bill line may still bill against the Purchase Order line it names, in the line's own quantity. */
export interface BillableFacts {
  /** The quantity the bill line claims. */
  billing: number;
  /** The order line's ordered quantity. */
  ordered: number;
  /** What the order line has received so far. */
  received: number;
  /** What approved Bills already bill against the same order line. */
  billedAlready: number;
  /** The order line's own description, so the refusal names the line a person can find. */
  description: string;
}

/**
 * 🔒 A Bill line may claim up to the ordered quantity its order line has left
 * to bill, and only that cap refuses (decision 0529). A vendor invoicing the
 * whole order on the first delivery is ordinary, so the part beyond what is
 * received is billed before receipt, which decision 0413 already supports for a
 * line nothing has been received on.
 */
export function overBilledProblem(facts: BillableFacts): string | null {
  const orderedLeft = round4(facts.ordered - facts.billedAlready);
  if (facts.billing > orderedLeft) {
    return `this bill claims ${facts.billing} of ${facts.description}, and the order has ${orderedLeft} left to bill of the ${facts.ordered} it ordered`;
  }
  return null;
}

/**
 * How much of a Bill line its order line's receipt already covers: the part
 * ahead of the receipt is billed before receipt.
 *
 * 🔨 It is not what 2050 clears at (decision 0529). The clearing leg is the
 * order's own price times the WHOLE claimed quantity, so each later receipt's
 * own 2050 credit nets its share. This figure answers how much of the claim the
 * goods have caught up with, which is what the open-2050 report reads.
 */
export function coveredQuantity(facts: { billing: number; received: number; billedAlready: number }): number {
  return Math.max(0, Math.min(facts.billing, round4(facts.received - facts.billedAlready)));
}

/**
 * 🔒 A Bill whose vendor invoice number already stands on another live Bill of the same vendor is warned about by name,
 * and the person confirms to keep both (T1041; decision 10860). The read is `GET` of this path; a Void Bill does not count.
 */
export const billDuplicateInvoicePath = (vendorCompanyId: string, vendorInvoiceNumber: string): string =>
  `/bills/duplicate-invoice/${encodeURIComponent(vendorCompanyId)}?number=${encodeURIComponent(vendorInvoiceNumber)}`;

/** The other live Bill carrying the same vendor's invoice number, or null where none does. */
export interface BillDuplicateInvoice {
  billId: string;
  billNumber: string;
}

/**
 * 🔨 `vendorInvoiceNumber` is the vendor's own number and `billNumber` is
 * ours; the two are never conflated when matching a payment. This is the one
 * comparison a matcher runs, and it refuses to be handed the other string.
 */
export function matchesVendorInvoice(bill: { billNumber: string; vendorInvoiceNumber: string }, candidate: { ourNumber?: string | null; vendorNumber?: string | null }): boolean {
  if (candidate.ourNumber !== undefined && candidate.ourNumber !== null) return candidate.ourNumber === bill.billNumber;
  if (candidate.vendorNumber !== undefined && candidate.vendorNumber !== null) return candidate.vendorNumber === bill.vendorInvoiceNumber;
  return false;
}

/**
 * 🔨 `dueDate` derives from `billDate` plus the Bill's own `paymentTerms`,
 * never re-read from the vendor's Company record afterwards — the Invoice
 * rule in the other direction (piece 22, Bill and landed costs). The day count
 * is the Invoice's own `termDays` and not a second reading of the same free
 * text: a second one stood here for a day, and it read anything it did not
 * recognise as thirty days where the Invoice's reads it as due on receipt
 * (found on the merge, 2026-09-15).
 */
export function dueDateFromTerms(billDate: Date, paymentTerms: string): Date {
  const due = new Date(billDate.getTime());
  due.setUTCDate(due.getUTCDate() + termDays(paymentTerms));
  return due;
}

/** A Bill saves with a vendor, the vendor's own number, a date and at least one line. */
export function billProblem(input: { vendorCompanyId?: string | null; vendorInvoiceNumber?: string | null; billDate?: string | null; paymentTerms?: string | null; lineCount: number }): string | null {
  if (!input.vendorCompanyId) return 'a bill names the vendor it is owed to; choose the vendor';
  if (!input.vendorInvoiceNumber || !input.vendorInvoiceNumber.trim()) return 'a bill carries the vendor’s own invoice number, which is never our bill number';
  if (!input.billDate || !/^\d{4}-\d{2}-\d{2}$/.test(input.billDate)) return 'a bill carries the vendor’s date as a calendar date; pick the date printed on the vendor’s invoice';
  if (!input.paymentTerms || !input.paymentTerms.trim()) return 'a bill carries the payment terms its due date derives from; choose the terms, and the due date follows from them';
  if (input.lineCount < 1) return 'a bill carries at least one line; add what the vendor is charging for';
  return null;
}

/**
 * 🔨 A Bill never receives the goods by itself (piece 23, Receiving): the
 * modal asks a person and the receive posts because they said so. This is
 * what the prompt shows — the order behind the bill and what is still owed.
 */
export interface DeliveryPrompt {
  purchaseOrderId: string;
  purchaseOrderNumber: string;
  /** False on a drop ship, which confirms a shipment instead of receiving. */
  receivable: boolean;
  outstanding: readonly { lineId: string; description: string; ordered: number; received: number; remaining: number }[];
}

/** One open `PaymentAllocation` against a Bill, as the Void refusal names it: the money's own reference and what it was. */
export interface BillAllocationFacts {
  /** `Payment` or `VendorCredit`, the two sources a Bill allocation has. */
  sourceType: string;
  /** The payment's reference or the credit's number; the refusal names it so the person can find the row to dissociate. */
  reference: string | null;
}

/**
 * 🔒 A Bill with an allocation against it is not voided (decision 0528). The
 * reversal is right for a bill raised in error; a bill the vendor has been paid
 * against would leave that payment pointing at a voided document, so the
 * refusal names the payment and says to dissociate it first. A Bill with no
 * open allocation voids as it always did.
 */
export function billVoidProblem(allocations: readonly BillAllocationFacts[]): string | null {
  if (allocations.length === 0) return null;
  const named = allocations.map((row) => `${row.sourceType === 'VendorCredit' ? 'vendor credit' : 'payment'} ${row.reference ?? 'with no reference'}`);
  const list = named.length === 1 ? named[0]! : `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]!}`;
  return `this bill still carries ${list}; dissociate it from the bill before voiding the bill`;
}

/** A bill that is already approved, void or opening is not approved again. */
export function billApprovalProblem(status: string, approvalStatus: string, isOpening: boolean): string | null {
  if (status === 'Void') return 'a void bill is not approved';
  if (isOpening) return 'an opening bill carries the cutover’s own ledger entry and is not posted again';
  if (approvalStatus === 'Pending') return 'the approval gate holds this bill; it posts once it is approved';
  if (approvalStatus === 'Rejected') return 'this bill was rejected; edit it and resubmit before it posts';
  if (status === 'Received') return 'this bill is already received and posted';
  return null;
}

/**
 * The answer the Bill's page reads (piece 24; decisions 0408, 0409). Declared
 * here rather than in the service, so the screen and the server hold the same
 * shape and a fixture missing a key the page reads fails the typecheck rather
 * than the page.
 */
export interface BillLineAnswer {
  id: string;
  lineNumber: number;
  itemId: string | null;
  /** The Item's own name, which the sheet's Item cell leads with and the edit form reopens on (T118; decision 0640). */
  itemName: string | null;
  sku: string | null;
  description: string;
  quantity: number;
  unitName: string;
  unitPriceCents: number;
  extendedCents: number;
  isLandedCost: boolean;
  allocatedLandedCostCents: number | null;
  accountId: string;
  accountNumber: string;
  /** 🔨 Derived every read against the tolerance in force, never stored (decision 0412). */
  variance: BillLineVariance;
  /** 🔒 Decision 9700: a line on 1500 or 1510 of a posted Bill offers Create Asset until it has made one (`billAssetProblem` answers null). */
  offersCreateAsset: boolean;
  /** 🔒 Decision 9700: the asset this line's Create Asset made, which the line reads as a link; null until then. */
  asset?: { id: string; name: string } | null;
}

/**
 * 🔒 Decision 9700: why a Bill line makes no asset, or null where Create Asset
 * runs. One line makes one asset; the line is on 1500 or 1510; and the Bill is
 * posted, because a Draft line can still change and a Void Bill capitalized
 * nothing. `status` is the Bill's stored status.
 */
export function billAssetProblem(facts: { status: string; accountNumber: string; assetName: string | null }): string | null {
  if (facts.assetName !== null) return `This line has already made the asset ${facts.assetName}, and each line of a bill makes one asset at most.`;
  if (!(FIXED_ASSET_ACCOUNT_NUMBERS as readonly string[]).includes(facts.accountNumber)) return `This line posts to ${facts.accountNumber}; Create Asset is offered on a line resolving to ${FIXED_ASSET_ACCOUNT_NUMBERS.join(' or ')}.`;
  if (facts.status === 'Draft') return 'This bill is still a Draft; Create Asset is offered once the bill is posted.';
  if (facts.status === 'Void') return 'This bill is void and capitalized nothing, so its lines make no asset.';
  return null;
}

/**
 * 🔒 Decision 9700: what the New Asset window opens on when a Bill line's
 * Create Asset is pressed. The line's description, its amount as the page reads
 * it and the Bill date, the organization as owner, and the Bill it came from.
 */
export interface BillAssetPrefill {
  billId: string;
  billNumber: string;
  lineId: string;
  lineNumber: number;
  name: string;
  acquiredOn: string;
  acquisitionCostCents: number;
  ownerCompanyId: string | null;
  ownerName: string | null;
}

/** The available credit a vendor's next Bill can draw on, in the two shapes it takes (piece 24, Vendor credits). */
export interface AvailableCreditRow {
  kind: 'VendorCredit' | 'Prepayment';
  id: string;
  reference: string;
  amountCents: number;
  dated: string;
}

/**
 * 🔒 One of a vendor's orders the Bill form offers to bill (T140; decision
 * 0711): an order `purchaseOrderBillProblem` clears, with what is left on it.
 */
export interface OpenPurchaseOrderRow {
  id: string;
  /** The number as the vendor holds it, the revision suffix included. */
  displayNumber: string;
  fulfillmentStatus: string;
  documentDate: string;
  /** The unbilled quantities at the order's own prices, in cents. */
  unbilledCents: number;
  unbilledLines: number;
}

/** One line of an order's unbilled remainder, as the form fills it and the build from the order writes it. */
export interface PurchaseOrderRemainderLine {
  sourceLineId: string;
  itemId: string | null;
  itemName: string | null;
  sku: string | null;
  description: string;
  quantity: number;
  unitId: string;
  unitName: string;
  unitPriceCents: number;
  accountId: string;
}

/**
 * 🔒 What is left to bill on one order (T140, T136; decision 0711): the lines
 * no approved Bill has claimed, each at its open quantity and the order's own
 * price, and the group dividers that still group something. The Bill form fills
 * its lines from it and `from-purchase-order` writes a Bill from it, so the two
 * raise the same lines.
 */
export interface PurchaseOrderRemainder {
  purchaseOrderId: string;
  displayNumber: string;
  vendorCompanyId: string;
  vendorName: string;
  lines: PurchaseOrderRemainderLine[];
  dividers: { name: string; position: number }[];
}

export interface BillAnswer {
  /** 🔒 T346 (decision 1515): the files attached to the bill, the vendor's own PDF a message from the accounting mailbox carried among them; absent from an older reply. */
  files?: DocumentFileRow[];
  /** 🔒 Why the document rests where it does, and a Draft's one primary action (T131, T223; decision 0713); absent from an older reply. */
  approval?: ApprovalView;
  /** 🔒 The group dividers among the lines, each at its place in the one reading order (decision 0650); absent from an older reply. */
  dividers?: DividerRow[];
  id: string;
  billNumber: string;
  vendorCompanyId: string;
  vendorName: string;
  vendorInvoiceNumber: string;
  sourcePurchaseOrderId: string | null;
  sourcePurchaseOrderNumber: string | null;
  billDate: string;
  dueDate: string;
  paymentTerms: string;
  /** 🔒 The notes to the vendor Region 7 prints (decision 0683); null where none are typed. */
  customerNotes: string | null;
  /** The stored member shadowed by the paid states, which derive from `amountPaid`. */
  status: string;
  approvalStatus: string;
  totalCents: number;
  amountPaidCents: number;
  landedTotalCents: number;
  /** 🔨 True when the bill carries no goods line: the freight is an expense and lands nothing. */
  landedCostExpensed: boolean;
  isOpening: boolean;
  /**
   * 🔒 The layout the bill prints on, resolved once beside the read (decision
   * 0567): view mode positions these region rows, so a region nudged in the
   * editor moves on the page as it moves on the sheet.
   */
  printLayout?: PrintSheetLayout | null;
  /** 🔒 The sheet both views read, as the printer's own HTML, in this same answer (T169). */
  printSheet?: PrintSheetHtml | null;
  /** 🔒 Why this viewer writes nothing on the document, or null where they may (decision 0687). */
  writeRefusal?: string | null;
  /** 🔒 Whether this viewer may approve the Pending bill now, the queue's own answer on this one document (decision 2863, after 1521); absent from an older reply, which reads as no. */
  canApprove?: boolean;
  lines: BillLineAnswer[];
  /** 🔨 A Bill never receives the goods by itself; this is what the modal asks about (piece 23, Receiving). */
  deliveryPrompt: DeliveryPrompt | null;
  /** The percentage the three-way match was measured against on this read (decision 0224). */
  varianceTolerancePercent: number;
  /** True where any line is outside the tolerance; the pill the Bill's page draws (decision 0412). */
  varianceFlagged: boolean;
  /** The vendor's available credit, so the page prompts before the Bill is paid rather than after. */
  availableCredit: AvailableCreditRow[];
}

/** One row of the Bill list; the status is the derived one, the paid states shadowing the stored member. */
export interface BillListRow {
  id: string;
  billNumber: string;
  vendorName: string;
  vendorInvoiceNumber: string;
  status: string;
  approvalStatus: string;
  total: number;
  dueDate: string;
}

/** The acts the Bill's footer offers, one set per status; the server refuses on its own terms whatever the page offers. */
export type BillAction = 'submit' | 'approve' | 'reject' | 'resubmit' | 'pay' | 'void';

/**
 * What Save and Submit answers (piece 24; decision 0526): where the gate put
 * the bill, and whether the submission posted it. 🔒 A submission the gate
 * clears — NotRequired or Approved — posts the bill in the same act, so it
 * leaves Draft for Received; one the gate holds waits Pending. `posted` false on
 * a cleared bill means the submitter holds no `accounting.post`, and the bill
 * waits for somebody who does to submit it.
 */
export type BillSubmissionAnswer = RoutingOutcome & { posted: boolean };

/** Which of the three things a submission did, as the page names it; never silence (decision 0559). */
export function billSubmissionOutcome(answer: { approvalStatus: RoutingOutcome['approvalStatus']; posted: boolean }): 'posted' | 'pending' | 'clearedNotPosted' {
  if (answer.approvalStatus === 'Pending') return 'pending';
  return answer.posted ? 'posted' : 'clearedNotPosted';
}

/**
 * 🔨 A Bill is never sent to anybody, so its first outward move is Submit and
 * the footer never reads Send (piece 24, Bills). A Draft submits; a Pending
 * bill offers the approval pair to somebody who can act; a Rejected one
 * resubmits; and anything not already void can be voided.
 */
export function billActions(facts: { status: string; approvalStatus: string; mayApprove: boolean }): BillAction[] {
  if (facts.status === 'Void') return [];
  const actions: BillAction[] = [];
  if (facts.approvalStatus === 'Pending') {
    if (facts.mayApprove) actions.push('approve', 'reject');
  } else if (facts.approvalStatus === 'Rejected') {
    actions.push('resubmit');
  } else if (facts.status === 'Draft') {
    actions.push('submit');
  }
  // 🔨 Decision 0429: Pay is offered exactly where the Pay Bills run would accept the bill, which is
  // Received or PartiallyPaid with something still open. The run's own list is the rule, mirrored here
  // rather than restated, so the screen never offers a button the route refuses.
  if (facts.status === 'Received' || facts.status === 'PartiallyPaid') actions.push('pay');
  actions.push('void');
  return actions;
}
